import React, { useState, useRef, useEffect } from "react";
import { FileDown, FileSpreadsheet } from "lucide-react";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

const ExportDropdown = ({ data, mode, startDate, period }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    function handleClickOutside(event) {
      if (ref.current && !ref.current.contains(event.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleExportExcelArea = async (data) => {
    if (!data || data.length === 0) return;

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Schedule");

    const getFormattedDate = (dateStr) => {
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "2-digit",
      });
    };

    const getISODate = (dateStr) => {
      if (!dateStr) return;
      const d = new Date(dateStr);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    // Filter data: only include shifts within the selected period
    const normalizeDate = (date) => {
      const d = new Date(date);
      d.setHours(0, 0, 0, 0);
      return d;
    };

    const normalizedStartDate = normalizeDate(startDate);
    // Calculate end date: startDate + (period - 1) days
    const endDate = new Date(normalizedStartDate);
    endDate.setDate(endDate.getDate() + (period - 1));
    const normalizedEndDate = normalizeDate(endDate);

    const filteredData = data.filter((item) => {
      const itemDate = normalizeDate(item.date);
      return itemDate >= normalizedStartDate && itemDate <= normalizedEndDate;
    });

    // Generate all dates in the period range (even if no shifts exist)
    const allPeriodDates = [];
    for (let i = 0; i < period; i++) {
      const date = new Date(normalizedStartDate);
      date.setDate(date.getDate() + i);
      allPeriodDates.push(getISODate(date));
    }

    // Use all period dates to ensure all columns are present
    const uniqueDates = allPeriodDates;

    const grouped = {};
    filteredData.forEach((item) => {
      const dept = item.loc_name || "Unknown";
      const name = item.user || "Unnamed";
      const date = getISODate(item.date);
      const shift = `${dept}\n${name}\n${item.start} - ${item.end}\n${
        item.break || ""
      }`;

      if (!grouped[dept]) grouped[dept] = {};
      if (!grouped[dept][name]) grouped[dept][name] = {};
      grouped[dept][name][date] = shift;
    });

    // ==== Title Row ====
    const startDateObj = new Date(uniqueDates[0]);
    const endDateObj = new Date(uniqueDates[uniqueDates.length - 1]);

    const formatFullDate = (date) =>
      date.toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });

    const titleText = `Schedule for Alertline Roster: ${formatFullDate(
      startDateObj
    )} to ${formatFullDate(endDateObj)}`;

    worksheet.insertRow(1, [titleText]);
    const colSpan = uniqueDates.length + 1;
    const lastColLetter = worksheet.getColumn(colSpan).letter;
    worksheet.mergeCells(`A1:${lastColLetter}1`);

    const titleCell = worksheet.getCell("A1");
    titleCell.font = { size: 35, bold: true, color: { argb: "FF000000" } };
    titleCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF000000" }, // white
    };
    titleCell.alignment = {
      vertical: "middle",
      horizontal: "center",
      wrapText: true,
    };
    worksheet.getRow(1).height = 50;

    // ==== Header Row ====
    const headers = ["Name", ...uniqueDates.map(getFormattedDate)];
    worksheet.insertRow(2, headers);

    worksheet.getRow(2).eachCell((cell) => {
      cell.font = { bold: true };
      cell.alignment = { vertical: "middle", horizontal: "center" };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFDCE3F0" },
      };
      cell.border = {
        top: { style: "thin", color: { argb: "FF000000" } },
        left: { style: "thin", color: { argb: "FF000000" } },
        bottom: { style: "thin", color: { argb: "FF000000" } },
        right: { style: "thin", color: { argb: "FF000000" } },
      };
    });

    // ==== Data Rows ====
    Object.entries(grouped).forEach(([dept, people]) => {
      worksheet.addRow([dept]);
      const deptRowIndex = worksheet.lastRow.number;

      worksheet.mergeCells(`A${deptRowIndex}:${lastColLetter}${deptRowIndex}`);
      const masterCell = worksheet.getCell(`A${deptRowIndex}`);

      masterCell.font = { bold: true, size: 25, color: { argb: "FFFFFFFF" } };
      masterCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF22C55E" }, // Tailwind green-500
      };
      masterCell.alignment = {
        vertical: "middle",
        horizontal: "center",
        wrapText: true,
      };
      worksheet.getRow(deptRowIndex).height = 40;

      Object.entries(people).forEach(([person, shifts]) => {
        const row = [person];
        uniqueDates.forEach((d) => {
          row.push(shifts[d] || "");
        });
        worksheet.addRow(row);
      });
    });

    // ==== Cell Styling ====
    worksheet.eachRow((row, rowNumber) => {
      const isTitle = rowNumber === 1;
      const isHeader = rowNumber === 2;
      const isDeptRow =
        row.getCell(1).isMerged &&
        rowNumber !== 1 &&
        worksheet.getCell(`A${rowNumber}`).value !== null;

      row.eachCell((cell) => {
        const value = cell.value || "";
        const isEmpty = value === "";

        let bgColor = "FFFFFFFF";
        if (isTitle) bgColor = "FFFFFFFF";
        else if (isHeader) {
          bgColor = "FFDCE3F0";
          row.height = 30; // Set header row height
        } else if (isDeptRow) bgColor = "FF22C55E"; // green-500
        else if (!isEmpty) bgColor = "FFEBF7E1";

        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: bgColor },
        };

        cell.font = isDeptRow
          ? { color: { argb: "FFFFFFFF" }, bold: true, size: 25 }
          : isTitle
          ? { color: { argb: "FF000000" }, bold: true, size: 25 }
          : { color: { argb: "FF000000" } };

        cell.alignment = {
          vertical: isDeptRow || isHeader || isTitle ? "middle" : "top",
          horizontal: isDeptRow || isHeader || isTitle ? "center" : "left",
          wrapText: true,
        };

        cell.border = {
          top: { style: "thin", color: { argb: "FF000000" } },
          left: { style: "thin", color: { argb: "FF000000" } },
          bottom: { style: "thin", color: { argb: "FF000000" } },
          right: { style: "thin", color: { argb: "FF000000" } },
        };
      });
    });

    // ==== Outer Border ====
    const rowCount = worksheet.rowCount;
    const colCount = worksheet.columnCount;

    for (let row = 1; row <= rowCount; row++) {
      for (let col = 1; col <= colCount; col++) {
        const cell = worksheet.getCell(row, col);
        if (row === 1)
          cell.border.top = { style: "medium", color: { argb: "FF000000" } };
        if (row === rowCount)
          cell.border.bottom = { style: "medium", color: { argb: "FF000000" } };
        if (col === 1)
          cell.border.left = { style: "medium", color: { argb: "FF000000" } };
        if (col === colCount)
          cell.border.right = { style: "medium", color: { argb: "FF000000" } };
      }
    }

    // Column width
    worksheet.columns.forEach((col, i) => {
      col.width = i === 0 ? 20 : 30;
    });

    // Freeze pane (below title and header)
    worksheet.views = [{ state: "frozen", ySplit: 2 }];

    // Save
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), "schedule-by-area.xlsx");
  };

  const handleExportExcelMember = async (data) => {
    if (!data || data.length === 0) return;

    // Helper functions
    const getFormattedDate = (dateStr) => {
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "2-digit",
      });
    };

    const getISODate = (dateStr) => {
      if (!dateStr) return;
      const d = new Date(dateStr);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    const calculateShiftHours = (start, end, breakTime) => {
      if (!start || !end) return 0;
      const [startH, startM] = start.split(":").map(Number);
      const [endH, endM] = end.split(":").map(Number);
      const startDate = new Date(0, 0, 0, startH, startM);
      const endDate = new Date(0, 0, 0, endH, endM);
      let hours = (endDate - startDate) / (1000 * 60 * 60);

      if (hours < 0) hours = 24 + hours;

      if (breakTime) {
        const breakMinutes = parseInt(breakTime, 10) || 0;
        hours -= breakMinutes / 60;
      }
      return hours;
    };

    // Filter data: only include shifts within the selected period
    // Normalize dates to compare only date part (YYYY-MM-DD) without time/timezone
    const normalizeDate = (date) => {
      const d = new Date(date);
      d.setHours(0, 0, 0, 0);
      return d;
    };

    const normalizedStartDate = normalizeDate(startDate);
    // Calculate end date: startDate + (period - 1) days
    // For period=7, we want days 0-6 (7 days total)
    const endDate = new Date(normalizedStartDate);
    endDate.setDate(endDate.getDate() + (period - 1));
    const normalizedEndDate = normalizeDate(endDate);

    const filteredData = data.filter((item) => {
      const itemDate = normalizeDate(item.date);
      return itemDate >= normalizedStartDate && itemDate <= normalizedEndDate;
    });

    if (filteredData.length === 0) {
      alert("No shifts found after the selected start date.");
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Schedule");

    // Generate all dates in the period range (even if no shifts exist)
    const allPeriodDates = [];
    for (let i = 0; i < period; i++) {
      const date = new Date(normalizedStartDate);
      date.setDate(date.getDate() + i);
      allPeriodDates.push(getISODate(date));
    }

    // Get unique sorted dates from filtered data, but limit to period range
    const dataDates = [
      ...new Set(filteredData.map((item) => getISODate(item.date))),
    ].filter((date) => allPeriodDates.includes(date));
    
    // Use all period dates to ensure all columns are present
    const uniqueDates = allPeriodDates;

    // Group by user and calculate total hours
    const groupedByUser = {};
    filteredData.forEach((item) => {
      const name = item.user || "Unnamed";
      const dept = item.loc_name || "Unknown";
      const date = getISODate(item.date);
      const shift = `${dept}\n${item.start} - ${item.end}\n${item.break || ""}`;
      const hours = calculateShiftHours(item.start, item.end, item.break);

      if (!groupedByUser[name])
        groupedByUser[name] = { shifts: {}, totalHours: 0 };
      groupedByUser[name].shifts[date] = shift;
      groupedByUser[name].totalHours += hours;
    });

    // ==== Title Row ====
    const startDateObj = new Date(uniqueDates[0]);
    const endDateObj = new Date(uniqueDates[uniqueDates.length - 1]);

    const formatFullDate = (date) =>
      date.toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });

    const titleText = `Schedule for Alertline Roster: ${formatFullDate(
      startDateObj
    )} to ${formatFullDate(endDateObj)}`;

    worksheet.insertRow(1, [titleText]);
    const colSpan = uniqueDates.length + 2; // +2 for Name and Total Hours
    const lastColLetter = worksheet.getColumn(colSpan).letter;
    worksheet.mergeCells(`A1:${lastColLetter}1`);

    const titleCell = worksheet.getCell("A1");
    titleCell.font = { size: 20, bold: true, color: { argb: "FF000000" } };
    titleCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFFFFFFF" },
    };
    titleCell.alignment = {
      vertical: "middle",
      horizontal: "center",
      wrapText: true,
    };
    worksheet.getRow(1).height = 50;

    // ==== Header Row ====
    const headers = [
      "Name",
      "Total Hours",
      ...uniqueDates.map(getFormattedDate),
    ];
    worksheet.insertRow(2, headers);

    worksheet.getRow(2).eachCell((cell) => {
      cell.font = { bold: true };
      cell.alignment = { vertical: "middle", horizontal: "center" };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFDCE3F0" },
      };
      cell.border = {
        top: { style: "thin", color: { argb: "FF000000" } },
        left: { style: "thin", color: { argb: "FF000000" } },
        bottom: { style: "thin", color: { argb: "FF000000" } },
        right: { style: "thin", color: { argb: "FF000000" } },
      };
    });

    // ==== Data Rows ====
    // Sort users alphabetically
    const sortedUsers = Object.keys(groupedByUser).sort((a, b) =>
      a.localeCompare(b)
    );
    sortedUsers.forEach((user) => {
      const userData = groupedByUser[user];
      const row = [user, userData.totalHours.toFixed(2)];
      uniqueDates.forEach((d) => {
        row.push(userData.shifts[d] || "");
      });
      worksheet.addRow(row);
    });

    // ==== Cell Styling ====
    worksheet.eachRow((row, rowNumber) => {
      const isTitle = rowNumber === 1;
      const isHeader = rowNumber === 2;

      row.eachCell((cell) => {
        const value = cell.value || "";
        const isEmpty = value === "";

        let bgColor = "FFFFFFFF";
        if (isTitle) bgColor = "FFFFFFFF";
        else if (isHeader) bgColor = "FFDCE3F0";
        else if (!isEmpty) bgColor = "FFEBF7E1";

        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: bgColor },
        };

        cell.font = isTitle
          ? { color: { argb: "FF000000" }, bold: true, size: 20 }
          : isHeader
          ? { color: { argb: "FF000000" }, bold: true }
          : { color: { argb: "FF000000" } };

        cell.alignment = {
          vertical: isTitle || isHeader ? "middle" : "top",
          horizontal: isTitle || isHeader ? "center" : "left",
          wrapText: true,
        };

        cell.border = {
          top: { style: "thin", color: { argb: "FF000000" } },
          left: { style: "thin", color: { argb: "FF000000" } },
          bottom: { style: "thin", color: { argb: "FF000000" } },
          right: { style: "thin", color: { argb: "FF000000" } },
        };
      });
    });

    // ==== Outer Border ====
    const rowCount = worksheet.rowCount;
    const colCount = worksheet.columnCount;

    for (let row = 1; row <= rowCount; row++) {
      for (let col = 1; col <= colCount; col++) {
        const cell = worksheet.getCell(row, col);
        if (row === 1)
          cell.border.top = { style: "medium", color: { argb: "FF000000" } };
        if (row === rowCount)
          cell.border.bottom = { style: "medium", color: { argb: "FF000000" } };
        if (col === 1)
          cell.border.left = { style: "medium", color: { argb: "FF000000" } };
        if (col === colCount)
          cell.border.right = { style: "medium", color: { argb: "FF000000" } };
      }
    }

    // Column width
    worksheet.columns.forEach((col, i) => {
      col.width = i === 0 ? 20 : i === 1 ? 12 : 30;
    });

    // Freeze pane (below title and header)
    worksheet.views = [{ state: "frozen", ySplit: 2 }];

    // Save
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), "schedule-by-user.xlsx");
  };

  const handleExportCSV = () => {
    const csvRows = [];
    const headers = Object.keys(data[0]);
    csvRows.push(headers.join(","));

    for (const row of data) {
      const values = headers.map(
        (header) => `"${String(row[header] || "").replace(/"/g, '""')}"`
      );
      csvRows.push(values.join(","));
    }

    const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = "schedule-export.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleExportExcel = (data) => {
    if (mode == "location") {
      handleExportExcelArea(data);
    } else {
      handleExportExcelMember(data);
    }
  };

  return (
    <div className="relative md:hidden lg:block inline-block" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="px-4 py-2 rounded-lg font-semibold transition-colors duration-200 bg-gray-100 text-gray-700 hover:bg-gray-200 text-sm flex items-center"
      >
        <FileDown className="w-4 h-4 mr-2" />
        Export
      </button>

      {open && (
        <div className="absolute left-0 mt-2 w-60 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
          <button
            onClick={() => {
              setOpen(false);
              handleExportExcel(data);
            }}
            className="flex items-center w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <FileSpreadsheet className="w-4 h-4 mr-2 text-gray-600" />
            Export as Excel
          </button>
          <button
            onClick={() => {
              setOpen(false);
              handleExportCSV();
            }}
            className="flex items-center w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-b-lg transition-colors"
          >
            <FileDown className="w-4 h-4 mr-2 text-gray-600" />
            Export as CSV
          </button>
        </div>
      )}
    </div>
  );
};

export default ExportDropdown;
